import React, { Component } from "react";
import { connect } from "react-redux";
import { pmaActions } from "_store/_actions";
import { propertyUtil } from "views/Pma/PropertyUtil";
import DashboardHeaderSubtitleEditorModal, {
  MAX_TITLE_LENGTH,
  MAX_SUBTITLE_LENGTH,
} from "./DashboardHeaderSubtitleEditorModal";

class DashboardHeader extends Component {
  state = {
    modalOpen: false,
    titleEditValue: "",
    titleEditBaseline: "",
    subtitleEditValue: "",
    subtitleEditBaseline: "",
    saving: false,
    saveError: null,
    saveSuccess: null,
    titleDraft: null,
    subtitleDraft: null,
  };

  saveSuccessTimeoutId = null;

  componentDidMount() {
    this.syncHeaderFromProps();
  }

  componentWillUnmount() {
    if (this.saveSuccessTimeoutId) {
      clearTimeout(this.saveSuccessTimeoutId);
      this.saveSuccessTimeoutId = null;
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.dashboardPriceRangeTitle !==
        this.props.dashboardPriceRangeTitle ||
      prevProps.dashboardPriceRangeSubTitle !==
        this.props.dashboardPriceRangeSubTitle ||
      prevProps.priceRangeDashBoardDetailsDTO !==
        this.props.priceRangeDashBoardDetailsDTO ||
      prevProps.useCity !== this.props.useCity
    ) {
      this.syncHeaderFromProps();
    }
  }

  syncHeaderFromProps = () => {
    const titleFromDto =
      this.props.priceRangeDashBoardDetailsDTO?.dashboardPriceRangeTitle ??
      this.props.dashboardPriceRangeTitle;
    const subtitleFromDto =
      this.props.priceRangeDashBoardDetailsDTO?.dashboardPriceRangeSubTitle ??
      this.props.dashboardPriceRangeSubTitle;
    this.setState({
      titleDraft: titleFromDto != null ? String(titleFromDto) : "",
      subtitleDraft:
        subtitleFromDto != null
          ? this.normalizeSubtitleText(String(subtitleFromDto))
          : "",
    });
  };

  normalizeSubtitleText = (subtitle) => {
    if (
      this.props.useCity &&
      subtitle ===
        "Below are three price ranges of homes that sold in your neighborhood over the last 12 months."
    ) {
      return "Below are three price ranges of homes that sold in your city over the last 12 months.";
    }
    return subtitle;
  };

  getTitleText = () => {
    if (this.state.titleDraft != null) {
      return String(this.state.titleDraft);
    }
    const fromDto =
      this.props.priceRangeDashBoardDetailsDTO?.dashboardPriceRangeTitle ??
      this.props.dashboardPriceRangeTitle;
    return fromDto != null ? String(fromDto) : "";
  };

  getSubtitleText = () => {
    if (this.state.subtitleDraft != null) {
      return this.normalizeSubtitleText(String(this.state.subtitleDraft));
    }
    const fromDto =
      this.props.priceRangeDashBoardDetailsDTO?.dashboardPriceRangeSubTitle ??
      this.props.dashboardPriceRangeSubTitle;
    return fromDto != null ? this.normalizeSubtitleText(String(fromDto)) : "";
  };

  isHeaderDirty = () => {
    const {
      titleEditValue,
      titleEditBaseline,
      subtitleEditValue,
      subtitleEditBaseline,
    } = this.state;
    return (
      String(titleEditValue || "") !== String(titleEditBaseline || "") ||
      String(subtitleEditValue || "") !== String(subtitleEditBaseline || "")
    );
  };

  openEditModal = () => {
    const title = this.getTitleText();
    const subtitle = this.getSubtitleText();
    this.setState({
      modalOpen: true,
      titleEditValue: title,
      titleEditBaseline: title,
      subtitleEditValue: subtitle,
      subtitleEditBaseline: subtitle,
      saveError: null,
      saveSuccess: null,
    });
  };

  closeEditModal = () => {
    if (this.state.saving || this.state.saveSuccess) {
      return;
    }
    this.setState({
      modalOpen: false,
      titleEditValue: "",
      titleEditBaseline: "",
      subtitleEditValue: "",
      subtitleEditBaseline: "",
      saveError: null,
      saveSuccess: null,
    });
  };

  requestCloseEditModal = () => {
    if (this.state.saving || this.state.saveSuccess) {
      return;
    }
    if (this.isHeaderDirty()) {
      const discard = window.confirm(
        "You have unsaved changes. Discard them and close?",
      );
      if (!discard) {
        return;
      }
    }
    this.closeEditModal();
  };

  clampLength = (value, max) => {
    const next = value ?? "";
    return typeof next === "string" && next.length > max
      ? next.slice(0, max)
      : next;
  };

  handleTitleChange = (e) => {
    const nextValue = this.clampLength(
      e?.target?.value ?? "",
      MAX_TITLE_LENGTH,
    );
    this.setState({ titleEditValue: nextValue, saveError: null });
  };

  handleSubtitleChange = (e) => {
    const nextValue = this.clampLength(
      e?.target?.value ?? "",
      MAX_SUBTITLE_LENGTH,
    );
    this.setState({ subtitleEditValue: nextValue, saveError: null });
  };

  showSaveSuccess = () => {
    if (this.saveSuccessTimeoutId) {
      clearTimeout(this.saveSuccessTimeoutId);
    }
    this.setState({ saveSuccess: "Saved", saveError: null });
    this.saveSuccessTimeoutId = setTimeout(() => {
      this.setState({
        modalOpen: false,
        titleEditValue: "",
        titleEditBaseline: "",
        subtitleEditValue: "",
        subtitleEditBaseline: "",
        saveSuccess: null,
      });
      this.saveSuccessTimeoutId = null;
    }, 2000);
  };

  onHeaderSave = async (payload = {}) => {
    const nextTitle =
      payload.title != null
        ? String(payload.title)
        : this.state.titleEditValue || "";
    const nextSubtitle =
      payload.subtitle != null
        ? String(payload.subtitle)
        : this.state.subtitleEditValue || "";
    const {
      priceRangeDashBoardDetailsDTO,
      useCity,
      updateDashBoardDetailsWithPriceRange,
      agentData,
      currentArea,
    } = this.props;

    if (this.state.saving || this.state.saveSuccess || !this.isHeaderDirty()) {
      return;
    }

    if (!priceRangeDashBoardDetailsDTO) {
      this.setState({
        saveError: "Unable to save. Dashboard details are missing.",
      });
      return;
    }

    if (
      nextTitle.length > MAX_TITLE_LENGTH ||
      nextSubtitle.length > MAX_SUBTITLE_LENGTH
    ) {
      return;
    }

    const previousTitle = this.state.titleDraft;
    const previousSubtitle = this.state.subtitleDraft;
    const nextDto = {
      ...priceRangeDashBoardDetailsDTO,
      isAgentEdited: true,
      dashboardPriceRangeTitle: nextTitle,
      dashboardPriceRangeSubTitle: nextSubtitle,
    };

    this.setState({
      saving: true,
      saveError: null,
      saveSuccess: null,
      titleEditValue: nextTitle,
      subtitleEditValue: nextSubtitle,
      titleDraft: nextTitle,
      subtitleDraft: nextSubtitle,
    });

    const success = await updateDashBoardDetailsWithPriceRange({
      listgenUserID: agentData?.listgenUserID,
      mlsAreaID: currentArea?.mlsAreaID,
      useCity: Boolean(useCity),
      priceRangeDashBoardDetailsDTO: nextDto,
    });

    if (success) {
      this.setState({
        saving: false,
        titleEditBaseline: nextTitle,
        subtitleEditBaseline: nextSubtitle,
      });
      this.showSaveSuccess();
      return;
    }

    this.setState({
      saving: false,
      saveError:
        "Unable to save dashboard title and subtitle. Please try again.",
      saveSuccess: null,
      titleDraft: previousTitle,
      subtitleDraft: previousSubtitle,
    });
  };

  getAreaTitle = () => {
    const { currentArea, hoData, agentData, useCity } = this.props;
    const { listgenUserID } = agentData;
    let { mlsNeighborhood, mlsAreaID, mlsCityName } = currentArea;
    const { splitAreas } = propertyUtil;

    const { subdivisionName } = hoData;
    let areaSplit = splitAreas(
      subdivisionName ? subdivisionName : mlsNeighborhood,
    );

    let yourOrOur = "Your";

    if (listgenUserID === "104504" || listgenUserID === "109860") {
      yourOrOur = "Our";
    }
    if (mlsAreaID === "NPA-E0600C") {
      mlsCityName = "St. Helena";
    }
    let neighborhoodTitleText = "Neighborhood";
    if (mlsAreaID === "NPA-E1200A") {
      neighborhoodTitleText = "Town";
    }
    let useSpecialAreaText = false;
    let specialAreaText = "";
    let titleContainerStyles = "";
    if (mlsAreaID === "MRN-A0300D") {
      useSpecialAreaText = true;
      specialAreaText = "Upper East Side - Corte Madera";
      titleContainerStyles = "prop-attributes-area-title-width-405";
    }

    if (useCity) {
      return (
        <div className="dashboard-area-title-container">
          <p className="dashboard-area-title">{mlsCityName}</p>
        </div>
      );
    } else if (useSpecialAreaText) {
      return (
        <div
          className={`dashboard-area-title-container ${titleContainerStyles}`}
        >
          <p className="dashboard-area-title">{specialAreaText}</p>
        </div>
      );
    } else if (areaSplit.length === 3) {
      return (
        <div className="dashboard-area-title-container">
          <p className="dashboard-area-title">{areaSplit[0]}</p>
          <p className="dashboard-area-title">{areaSplit[1]}</p>
          <p className="dashboard-area-title">{areaSplit[2]}</p>
        </div>
      );
    } else if (areaSplit.length === 2) {
      return (
        <div className="dashboard-area-title-container">
          <p className="dashboard-area-title">{areaSplit[0]}</p>
          <p className="dashboard-area-title">{areaSplit[1]}</p>
        </div>
      );
    } else if (mlsNeighborhood && mlsNeighborhood.length > 35) {
      return (
        <div className="dashboard-area-title-container">
          <p className="dashboard-area-title">Your Neighborhood</p>
        </div>
      );
    } else if (mlsAreaID === "CCA-5900E") {
      return (
        <div className="dashboard-area-title-container">
          <p className="dashboard-area-title">Black Diamond</p>
          <p className="dashboard-area-title">Chaparral Springs</p>
        </div>
      );
    } else {
      return (
        <div className="dashboard-area-title-container">
          <p className="dashboard-area-title">
            {subdivisionName ? subdivisionName : mlsNeighborhood}
          </p>
        </div>
      );
    }
  };

  render() {
    const { showEditButtons } = this.props;
    const {
      modalOpen,
      titleEditValue,
      subtitleEditValue,
      saving,
      saveError,
      saveSuccess,
    } = this.state;

    const titleText = this.getTitleText();
    const subtitleText = this.getSubtitleText();

    return (
      <div className="dashboard-header">
        {this.getAreaTitle()}
        <div className="dashboard-header-content">
          <div className="dashboard-title">
            {showEditButtons ? (
              <button
                type="button"
                className="dashboard-body-range-edit-btn"
                onClick={this.openEditModal}
                disabled={saving}
              >
                Edit Title & Subtitle
              </button>
            ) : null}
            <p className="dashboard-title-dark">{titleText}</p>
          </div>
          <div className="dashboard-subtitle-section">
            <div className="dashboard-info">
              <p>{subtitleText}</p>
            </div>
          </div>
        </div>
        {showEditButtons ? (
          <DashboardHeaderSubtitleEditorModal
            open={modalOpen}
            titleValue={titleEditValue}
            subtitleValue={subtitleEditValue}
            isDirty={this.isHeaderDirty()}
            onTitleChange={this.handleTitleChange}
            onSubtitleChange={this.handleSubtitleChange}
            onClose={this.requestCloseEditModal}
            onSave={this.onHeaderSave}
            saving={saving}
            saveSuccess={Boolean(saveSuccess)}
            saveError={saveError}
          />
        ) : null}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  updateDashBoardDetailsWithPriceRange: (payload) =>
    dispatch(pmaActions.updateDashBoardDetailsWithPriceRange(payload)),
});

export default connect(null, mapDispatchToProps)(DashboardHeader);
